/* @flow */
/**
 * Manages global geo stakeholder lookup data, filters, dropdowns, and API actions.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import { toggleSpinner } from '../dialogStates/dialogStatesSlice';
import { cryptoRand } from '../../helpers/common';
import { getFilterDropdownValuesFromResponse, getFilteredResults, sortDataAscendingConfig } from '../../helpers/dataHelpers';

const name = 'geoGlobal';

function createInitialState() {
  return {
    countryList: [],
    selectedFilters: {
      ruleName: [],
      destinationCountryCode: [],
      purchaseGroup: [],
      division: [],
      docType: [],
      plantCode: [],
      email: [],
      emailType: []
    },
    dataId: '',
    geoFilterId: '',
    geoConfigScreenData: null,
    unfilteredGeoConfigScreenData: null
  };
}

export const initialState = createInitialState();

/**
 * Stores country-code dropdown options for geo stakeholder configuration.
 */
function setCountryCodeDropdown(state: Object, action: Object) {
  return {
    ...state,
    countryList: action.payload
  };
}

/**
 * Refreshes the geo filter id to notify filter-dependent UI.
 */
function updateGeoFilterId(state: Object) {
  return {
    ...state,
    geoFilterId: cryptoRand().toString().slice(2, 10)
  };
}

/**
 * Updates one selected geo global filter list in state.
 */
function updateFilterValues(state: Object, action: Object) {
  if (Array.isArray(action.payload.values) && action.payload.filter) {
    return {
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        // Dynamic filter names allow this reducer to handle all configured filters.
        [action.payload.filter]: action.payload.values
      }
    };
  }
  return { ...state };
}

/**
 * Stores sorted dropdown filter values derived from geo stakeholder results.
 */
function updateFilterDropdownValues(state: Object, action: Object) {
  if (
    action.payload
      && Object.keys(action.payload).length > 0
  ) {
    return {
      ...state,
      filterDropdownValues: sortDataAscendingConfig(action.payload)
    };
  }
  return { ...state };
}

/**
 * Updates geo stakeholder table data while preserving unfiltered rows for reset/filtering.
 */
function updateGeoConfigScreen(state: Object, action: Object) {
  const payload = {
    data: action.payload.flattenedResponse,
    // Use filtered results when present, otherwise display all fetched rows.
    filteredResults: action.payload.filteredResults || action.payload.flattenedResponse
  };
  return {
    ...state,
    geoConfigScreenData: payload.filteredResults,
    unfilteredGeoConfigScreenData: payload.data,
    dataId: cryptoRand().toString().slice(2, 10)
  };
}

/**
 * Replaces visible geo stakeholder rows after filter application.
 */
function updateFilteredGeoConfigScreen(state: Object, action: Object) {
  const payload = {
    data: action.payload
  };
  return {
    ...state,
    geoConfigScreenData: payload.data,
    dataId: cryptoRand().toString().slice(2, 10)
  };
}

/**
 * Clears selected geo filters and restores unfiltered geo stakeholder rows.
 */
function clearGeoFilters(state: Object) {
  return {
    ...state,
    selectedFilters: {
      ...initialState.selectedFilters
    },
    geoConfigScreenData: state.unfilteredGeoConfigScreenData
  };
}

/**
 * Collects geo global reducers for slice creation.
 */
function createReducers() {
  return {
    setCountryCodeDropdown,
    updateGeoFilterId,
    updateFilterValues,
    updateFilterDropdownValues,
    updateGeoConfigScreen,
    updateFilteredGeoConfigScreen,
    clearGeoFilters
  };
}

export const reducers = createReducers();
export const slice = createSlice({ name, initialState, reducers });

export const actions = { ...slice.actions };

/**
 * Fetches active geo stakeholder email records and initializes filters/dropdowns.
 */
function getGeoGlobalResults() {
  return createAsyncThunk(
    `${name}/getGeoGlobalResults`,
    async ({
      callback
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      // Request all active records for the supported geo rule types in one call.
      const query = '?filter=isActive(true),rule(destCountryDivRule,docTypePurchaseGroupRule,divDocTypeRule,divPlantRule,docTypeRule)';
      request({
        api: 'getGeoStakeholdersEmail',
        method: 'get',
        routeParams: { query }
      }, dispatch).then((response) => {
        const flattenedResponse = response.data;
        const state = getState();
        const { selectedFilters } = state.geoGlobal;
        const filteredResults = getFilteredResults(
          // API nests geo rows under objects.geoResponse.
          flattenedResponse.objects.geoResponse,
          selectedFilters,
          'geoGlobal'
        );
        dispatch(actions.updateGeoConfigScreen(
          { flattenedResponse: flattenedResponse.objects.geoResponse, filteredResults }
        ));
        dispatch(actions.updateFilterDropdownValues(
          // Dropdown options come from the complete unfiltered geo response.
          getFilterDropdownValuesFromResponse(flattenedResponse.objects.geoResponse, [
            'ruleName',
            'destinationCountryCode',
            'division',
            'plantCode',
            'docType',
            'purchaseGroup',
            'email',
            'emailType'
          ], 'geoGlobal')
        ));
        dispatch(toggleSpinner(false));
        if (callback) {
          callback(response);
        }
      }).catch((error) => {
        dispatch(toggleSpinner(false));
        console.log(error);
        callback(error);
      });
    }
  );
}

/**
 * Adds geo stakeholder email details after shaping form rows for the selected rule.
 */
function addGeoStakeholderDetails() {
  return createAsyncThunk(
    `${name}/addGeoStakeholderDetails`,
    async ({
      data, callback, selectedRule
    }, { dispatch }) => {
      dispatch(toggleSpinner(true));
      const updatedData = data.map((row) => {
        const { value } = row.emailType;
        // ruleName is UI-only; the selected rule is sent separately in the request body.
        const { ruleName: _, ...updatedRow } = row;
        if (selectedRule === 'docTypePurchaseGroupRule' || selectedRule === 'docTypeRule') {
          const docTypeVal = row.docType.value;
          return ({
            ...updatedRow,
            emailType: value,
            docType: docTypeVal
          });
        }
        if (selectedRule === 'destCountryDivRule') {
          const destVal = row.destinationCountryCode.value;
          const divVal = row.division.value;
          // Country/division rules submit primitive dropdown values instead of option objects.
          return ({
            ...updatedRow,
            emailType: value,
            destinationCountryCode: destVal,
            division: divVal
          });
        }
        if (selectedRule === 'divDocTypeRule') {
          const divVal = row.division.value;
          const docTypeVal = row.docType.value;
          // Division/doc-type rules require both normalized dropdown values.
          return ({
            ...updatedRow,
            emailType: value,
            division: divVal,
            docType: docTypeVal
          });
        }
        if (selectedRule === 'divPlantRule') {
          const divVal = row.division.value;
          // Plant rules normalize division while leaving plant data from the row intact.
          return ({
            ...updatedRow,
            emailType: value,
            division: divVal
          });
        }
        return updatedRow;
      });

      const geoStakeholderRequestBody = {
        rule: selectedRule,
        geoData: updatedData
      };
      request({
        api: 'geoStakeholdersEmail',
        method: 'post',
        data: geoStakeholderRequestBody,
        cancellable: true
      }, dispatch).then((response) => {
        dispatch(toggleSpinner(false));
        callback(response.data);
      }).catch((error) => {
        dispatch(toggleSpinner(false));
        callback(null, error);
      });
    }
  );
}

/**
 * Applies geo global filters in Redux state and refreshes visible table rows.
 */
function applyFilters() {
  return createAsyncThunk(
    `${name}/applyFilters`,
    async (
      {
        payload
      },
      { getState, dispatch }
    ) => {
      dispatch(actions.updateFilterValues(payload));
      const state = getState();
      const { selectedFilters, unfilteredGeoConfigScreenData } = state.geoGlobal;
      // Filtering always starts from the cached unfiltered geo data set.
      const filteredResults = getFilteredResults(unfilteredGeoConfigScreenData, selectedFilters, 'geoGlobal');
      dispatch(actions.updateGeoFilterId());
      dispatch(actions.updateFilteredGeoConfigScreen(filteredResults));
    }
  );
}

/**
 * Clears geo global filters and restores the unfiltered result set.
 */
function clearFilters() {
  return createAsyncThunk(
    `${name}/clearFilters`,
    async (_, { dispatch }) => {
      dispatch(actions.clearGeoFilters());
    }
  );
}

/**
 * Sends edited geo stakeholder rows to the geoStakeholdersEmail update API.
 */
function updateGEOStakeholder() {
  return createAsyncThunk(
    `${name}/updateGEOStakeholder`,
    async ({
      data, callback
    }, { getState, dispatch }) => {
      if (data) {
        const updateRequestBody = {
          geoData: data
        };
        dispatch(toggleSpinner(true));
        request({
          api: 'geoStakeholdersEmail',
          method: 'put',
          data: updateRequestBody,
          cancellable: true
        },
        dispatch,
        getState).then((response) => {
          dispatch(toggleSpinner(false));
          callback(response.data);
        })
          .catch((error) => {
            dispatch(toggleSpinner(false));
            callback(null, error);
          });
      }
    }
  );
}

/**
 * Creates geo global async thunks exposed alongside slice actions.
 */
function createExtraActions() {
  return {
    getGeoGlobalResults: getGeoGlobalResults(),
    addGeoStakeholderDetails: addGeoStakeholderDetails(),
    applyFilters: applyFilters(),
    clearFilters: clearFilters(),
    updateGEOStakeholder: updateGEOStakeholder()
  };
}

const extraActions = createExtraActions();

export const geoGlobalActions = { ...actions, ...extraActions };
export const geoGlobalReducer = slice.reducer;
